import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Search, Send, ArrowUpDown, Users } from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";

const RISK_COLORS = {
  high: { bg: "bg-red-500/10", text: "text-red-400", label: "Risque" },
  medium: { bg: "bg-amber-500/10", text: "text-amber-400", label: "Surveiller" },
  low: { bg: "bg-emerald-500/10", text: "text-emerald-400", label: "OK" },
};

export default function Pilotes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [riskFilter, setRiskFilter] = useState("all");
  const [sortBy, setSortBy] = useState("score-asc");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/saas/pilotes");
      setItems(data.pilotes || []);
    } catch {
      toast.error("Impossible de charger les pilotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    let f = items;
    if (riskFilter !== "all") f = f.filter((p) => p.health?.risk === riskFilter);
    if (q.trim()) {
      const s = q.toLowerCase();
      f = f.filter((p) => (p.email || "").toLowerCase().includes(s) || (p.full_name || "").toLowerCase().includes(s));
    }
    const sorters = {
      "score-asc": (a, b) => (a.health?.score || 0) - (b.health?.score || 0),
      "score-desc": (a, b) => (b.health?.score || 0) - (a.health?.score || 0),
      "chantiers-desc": (a, b) => (b.health?.chantiers_count || 0) - (a.health?.chantiers_count || 0),
      "email-asc": (a, b) => (a.email || "").localeCompare(b.email || ""),
    };
    return [...f].sort(sorters[sortBy] || sorters["score-asc"]);
  }, [items, q, riskFilter, sortBy]);

  const counts = useMemo(() => ({
    all: items.length,
    high: items.filter((p) => p.health?.risk === "high").length,
    medium: items.filter((p) => p.health?.risk === "medium").length,
    low: items.filter((p) => p.health?.risk === "low").length,
  }), [items]);

  return (
    <ConsoleShell
      title="Pilotes"
      breadcrumb={["GABRYX", "Pilotes"]}
      actions={
        <button onClick={refresh} disabled={loading} className="text-xs border border-white/15 px-3 py-1.5 hover:bg-white/5 transition flex items-center gap-1.5" data-testid="pilotes-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      }
    >
      <div className="px-5 lg:px-8 py-6 space-y-4 max-w-[1600px]">
        {/* Filters bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            {["all", "high", "medium", "low"].map((r) => {
              const active = riskFilter === r;
              const color = r === "all" ? "white" : RISK_COLORS[r]?.text.replace("text-", "");
              return (
                <button
                  key={r}
                  onClick={() => setRiskFilter(r)}
                  data-testid={`filter-${r}`}
                  className={`text-xs px-3 py-1.5 border transition ${active ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:text-white hover:bg-white/5"}`}
                >
                  {r === "all" ? "Tous" : RISK_COLORS[r]?.label}
                  <span className="ml-1.5 font-mono opacity-60">{counts[r]}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher email, nom…"
                data-testid="pilotes-search"
                className="bg-[#0f0f0f] border border-white/15 pl-8 pr-3 py-1.5 text-xs w-64 focus:border-white/40 outline-none font-mono"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="pilotes-sort"
              className="bg-[#0f0f0f] border border-white/15 px-3 py-1.5 text-xs focus:border-white/40 outline-none"
            >
              <option value="score-asc">Score ↑ (risque d'abord)</option>
              <option value="score-desc">Score ↓ (champions d'abord)</option>
              <option value="chantiers-desc">Plus de chantiers</option>
              <option value="email-asc">Email A→Z</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="border border-white/10 bg-[#0f0f0f]" data-testid="pilotes-table">
          {loading ? (
            <div className="p-12 text-center text-zinc-500 text-sm">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-6 h-6 mx-auto mb-3 text-zinc-600" />
              <div className="font-display text-base mb-1">Aucun pilote</div>
              <p className="text-xs text-zinc-500">{items.length === 0 ? "CHANTIX n'a aucun pilote inscrit pour l'instant." : "Aucun pilote ne correspond aux filtres."}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10">
                  <th className="text-left px-5 py-3 font-normal">Pilote</th>
                  <th className="text-left px-3 py-3 font-normal">Email</th>
                  <th className="text-right px-3 py-3 font-normal">Chantiers</th>
                  <th className="text-center px-3 py-3 font-normal">Feedback</th>
                  <th className="text-center px-3 py-3 font-normal">Statut</th>
                  <th className="text-right px-3 py-3 font-normal">Score</th>
                  <th className="text-right px-5 py-3 font-normal">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const risk = p.health?.risk || "low";
                  const c = RISK_COLORS[risk];
                  const key = p.user_id || p.id || p.email;
                  return (
                    <tr key={key} className="border-b border-white/5 hover:bg-white/5 transition" data-testid={`pilote-row-${key}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-white/10 grid place-items-center text-[10px] font-bold uppercase">
                            {((p.full_name || p.email || "?")[0] || "?")}
                          </div>
                          <div className="font-medium">{p.full_name || p.name || "—"}</div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-zinc-400 font-mono">{p.email || "—"}</td>
                      <td className="px-3 py-3 text-right">{p.health?.chantiers_count ?? 0}</td>
                      <td className="px-3 py-3 text-center text-xs">{p.health?.has_feedback ? "✓" : "—"}</td>
                      <td className="px-3 py-3 text-center text-xs">{p.health?.is_active ? <span className="text-emerald-400">actif</span> : <span className="text-zinc-500">inactif</span>}</td>
                      <td className="px-3 py-3 text-right">
                        <span className={`inline-flex items-center text-[10px] font-mono uppercase tracking-[0.1em] px-2 py-0.5 ${c.bg} ${c.text}`}>
                          {p.health?.score ?? 0} · {c.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Link
                          to="/app/chat/clara"
                          state={{ prefill: `Rédige-moi un email de réengagement pour le pilote ${p.full_name || p.email}, score ${p.health?.score}/100, ${p.health?.chantiers_count} chantiers créés, ${p.health?.has_feedback ? "a laissé un feedback" : "sans feedback"}, ${p.health?.is_active ? "compte actif" : "compte inactif"}.` }}
                          data-testid={`pilote-action-${key}`}
                          className="text-[11px] text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
                        >
                          Réengager <Send className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="text-[10px] text-zinc-600 font-mono">
          {filtered.length} pilote{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur {items.length}
        </div>
      </div>
    </ConsoleShell>
  );
}
